import {
  forwardRef,
  useEffect,
  useRef,
  type TextareaHTMLAttributes,
} from "react";
import "./common.css";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  /** Grow to fit content (used by the chat composer + field editors). */
  autoGrow?: boolean;
  maxAutoGrow?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    {
      label,
      hint,
      error,
      autoGrow = false,
      maxAutoGrow = 320,
      id,
      className = "",
      value,
      onChange,
      ...rest
    },
    ref,
  ) {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const textareaId = id ?? rest.name;

    const resize = (el: HTMLTextAreaElement | null) => {
      if (!autoGrow || !el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, maxAutoGrow)}px`;
    };

    useEffect(() => {
      resize(innerRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, autoGrow]);

    return (
      <div>
        {label && (
          <label className="field-label" htmlFor={textareaId}>
            {label}
          </label>
        )}
        <textarea
          ref={(node) => {
            innerRef.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
          }}
          id={textareaId}
          className={`textarea ${error ? "textarea--error" : ""} ${className}`}
          value={value}
          onChange={(e) => {
            resize(e.currentTarget);
            onChange?.(e);
          }}
          aria-invalid={error ? true : undefined}
          {...rest}
        />
        {error ? (
          <div className="field-error">{error}</div>
        ) : hint ? (
          <div className="field-hint">{hint}</div>
        ) : null}
      </div>
    );
  },
);
